import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from 'antd';

import { useAuth, usePost } from 'hooks';
import { Button, NetPromoterScore } from 'components/common';
import { largeWarning, exportSearch } from 'services/api';
import {
  getSearchRequest,
  getHasResults,
  getLastSuccessfulSearch,
  getSearchView,
  getBlockedForExport,
  isPdfExportRunning,
  getFilters,
} from 'selectors/search';

import { setPdfExportStatus } from 'actions/search';
import { DisabledButtonWrapper } from 'components/common/styled.js';
import Export from 'components/Export';
import { downloadType } from 'components/Export/Summary/constants';
import ListAction from './List';
import { ActionsContainer } from '../styled';
import {
  handleExportNotification,
  handleFailedExportNotification,
} from './notifications';
import { searchOrigin, view as viewConstants } from '../constants';
import { ResearcherSearch } from '../helpers';
import { RemoveNonEmptyDate } from 'components/Search/Filters/keys';
import {
  ewrcaDescription,
  ewrcaOrganisationDescription,
} from 'reducers/notifications';
import { isFullTrial } from 'utils/helpers/license';
import { isExcluded } from 'utils/search';

const Actions = props => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [canUseNps, setCanUseNps] = useState(false);
  const [{ res, loading, error }, postExport] = usePost({});
  const { res: searchRes, loading: searching } = useSelector(getSearchRequest);
  const hasResults = useSelector(getHasResults);
  const isPdfExportStarted = useSelector(isPdfExportRunning);
  const lastSuccessfulSearch = useSelector(getLastSuccessfulSearch);
  const view = useSelector(getSearchView);
  const blockedForExport = useSelector(getBlockedForExport);
  const allFilters = useSelector(getFilters);
  const searchResRef = useRef({});

  const tmpFilters = useMemo(() => {
    const next = { ...allFilters };
    RemoveNonEmptyDate(next);
    // Activities tab can't select Profile, so it should not be present in
    // structuredSearch when exporting from the Activities view.
    if (view === viewConstants.activity && next?.category) {
      const { profile, ...categoryWithoutProfile } = next.category;
      next.category = categoryWithoutProfile;
    }
    return next;
  }, [allFilters, view]);

  const handleOpenNps = () => {
    setCanUseNps(true);
  };

  const handleDismissNps = () => {
    setCanUseNps(false);
  };

  const handleExport = additionalParams => {
    searchResRef.current = searchRes;
    const { largeExport, organisationOnlyExportEwrca } = searchRes;
    const {
      query,
      highlightQuery,
      lists,
      nestedQueries,
      organisationLists,
      excludeRecentlyExportedEmails,
    } = lastSuccessfulSearch;

    let ewrca = searchRes.ewrca;
    if (additionalParams.isOrganisationOnlyExport) {
      ewrca = organisationOnlyExportEwrca;
    }

    postExport({
      url: ewrca ? largeWarning('UniversalSearch') : exportSearch,
      body: {
        query,
        highlightQuery,
        orderBy: 'desc',
        largeExport,
        filters: {
          lists: lists?.filter(x => !isExcluded(x)),
          excludeLists: lists?.filter(isExcluded),
          organisationLists: organisationLists?.filter(x => !isExcluded(x)),
          organisationExcludeLists: organisationLists?.filter(isExcluded),
          excludeRecentlyExportedEmails,
        },
        isOrganisationExport: additionalParams.isOrganisationOnlyExport,
        structuredSearch: JSON.stringify(tmpFilters),
        nestedQueries,
        searchOrigin:
          view === viewConstants.activity
            ? searchOrigin.activitySearch
            : !ResearcherSearch(view)
            ? searchOrigin.organisationSearch
            : searchOrigin.researcherSearch,
        ...additionalParams,
      },
    });
  };

  useEffect(() => {
    if (res) {
      handleExportNotification(handleOpenNps);
    }
  }, [res]);

  useEffect(() => {
    if (error) {
      handleFailedExportNotification(error.status);
    }
  }, [error]);

  const handlePdfExport = () => {
    dispatch(setPdfExportStatus(true));
  };

  const disabled =
    searching || !lastSuccessfulSearch.query || !hasResults || blockedForExport;

  const exportButton = (
    <Export
      buttonTestId='search-export-data-button'
      disabled={loading || disabled || searchRes.ewrca}
      ewrca={!blockedForExport && searchRes.ewrca}
      downloadType={
        view === viewConstants.activity
          ? downloadType.activitySearch
          : !ResearcherSearch(view)
          ? downloadType.organisationSearch
          : downloadType.researcherSearch
      }
      onExport={handleExport}
      style={{ marginTop: '2px' }}
      ewrcaTooltip={ewrcaDescription}
      showExportTemplateSection={true}
    >
      Export Data
    </Export>
  );

  const exportOrganisationsButton = (
    <Export
      buttonTestId='search-export-organisations-button'
      style={{ marginLeft: 5 }}
      onExport={params =>
        handleExport({ ...params, isOrganisationOnlyExport: true })
      }
      disabled={loading || disabled || searchRes.organisationOnlyExportEwrca}
      downloadType={downloadType.organisationOnlyExport}
      ewrca={!blockedForExport && searchRes.organisationOnlyExportEwrca}
      canUseCredits={false}
      ewrcaTooltip={ewrcaOrganisationDescription}
    >
      Export Organisations
    </Export>
  );

  const pdfExportButton = !isFullTrial(user) && (
    <Button
      style={{ marginLeft: 5 }}
      size='small'
      loading={isPdfExportStarted}
      onClick={handlePdfExport}
      disabled={loading || disabled || isPdfExportStarted}
    >
      {isPdfExportStarted ? ' Generating PDF...' : 'Export PDF'}
    </Button>
  );

  const canExport = !user.isRateLimited && !user.disableExport;

  return (
    <ActionsContainer>
      {view !== viewConstants.activity && (
        <ListAction
          {...props}
          disabled={disabled}
          blockedForExport={blockedForExport}
        />
      )}
      {canExport &&
        (blockedForExport ? (
          <div className='exportOptions'>
            <Tooltip
              placement='topRight'
              title='You need to update the search to reactivate export'
            >
              <DisabledButtonWrapper>{exportButton}</DisabledButtonWrapper>
            </Tooltip>
            {view === viewConstants.organisation && (
              <Tooltip
                placement='topRight'
                title='You need to update the search to reactivate export'
              >
                <DisabledButtonWrapper>
                  {exportOrganisationsButton}
                </DisabledButtonWrapper>
              </Tooltip>
            )}
          </div>
        ) : (
          <div className='exportOptions'>
            {exportButton}
            {view === viewConstants.organisation && exportOrganisationsButton}
            {(view === viewConstants.visualisation ||
              view === viewConstants.organisationInsights) &&
              pdfExportButton}
          </div>
        ))}
      {canUseNps && <NetPromoterScore onDismiss={handleDismissNps} />}
    </ActionsContainer>
  );
};

export default Actions;
